/**
 * Extract inline-renderable card targets from an assistant message: detects `/function/<id>` and `/property/<id>` URLs (absolute or path-only) and returns their ids plus a cleaned text body, so the chat UI renders the matching card inline instead of leaving a bare link. Pure / browser-safe. The backend never strips these URLs -- SMS / WhatsApp users still receive them verbatim -- so stripping is a UI-channel-only concern invoked from the Message component, not from any flow handler.
 */

import { matchEnd } from '@federa/const/regex-match-span';

const ID_CHARS = '[A-Za-z0-9_-]+';

/** Match `/function/<id>` or `https://.../function/<id>`. Trailing period / comma is consumed. */
const FUNCTION_URL_RE = new RegExp(`(?:https?://[^\\s)]+)?/function/(${ID_CHARS})[.,]?`, 'i');

/** Match `/property/<id>` or `https://.../property/<id>`. Trailing period / comma is consumed. */
const PROPERTY_URL_RE = new RegExp(`(?:https?://[^\\s)]+)?/property/(${ID_CHARS})[.,]?`, 'i');

/** Match the lead-in phrases the AI typically uses so the cleaned text doesn't end with a dangling ":". */
const FUNCTION_LEAD_RE = /(?:the\s+)?(?:confirmation\s+)?link\s+(?:to\s+confirm[^:]*:|is\s*:?)\s*$/i;
const PROPERTY_LEAD_RE = /property\s+page\s*:\s*$/i;

interface InlineCardExtraction {
	cleanedText: string;
	functionExecutionId: string | null;
	propertyId: string | null;
}

/** Strip the matched URL and any preceding lead-in phrase, leaving readable prose. */
function stripWithLead(text: string, match: RegExpExecArray, leadRe: RegExp): string {
	const before = text.slice(0, match.index);
	const after = text.slice(matchEnd(match));
	const beforeTrimmed = before.replace(leadRe, '');
	return `${beforeTrimmed}${after}`;
}

export function extractInlineCards(content: string): InlineCardExtraction {
	let cleanedText = content;
	let functionExecutionId: string | null = null;
	let propertyId: string | null = null;

	const fnMatch = FUNCTION_URL_RE.exec(cleanedText);
	if (fnMatch?.[1]) {
		functionExecutionId = fnMatch[1];
		cleanedText = stripWithLead(cleanedText, fnMatch, FUNCTION_LEAD_RE);
	}

	const propMatch = PROPERTY_URL_RE.exec(cleanedText);
	if (propMatch?.[1]) {
		propertyId = propMatch[1];
		cleanedText = stripWithLead(cleanedText, propMatch, PROPERTY_LEAD_RE);
	}

	return { cleanedText: cleanedText.trim(), functionExecutionId, propertyId };
}
